import { useEffect, useMemo, useState, useRef } from 'react';
import { sessionSelectors, useSessionStore } from '../store/sessionStore';

export const TopNav = () => {
  const projects = useSessionStore(sessionSelectors.projects);
  const activeProjectId = useSessionStore(sessionSelectors.activeProjectId);
  const activeProject = useMemo(
    () => projects.find((project) => project.id === activeProjectId),
    [projects, activeProjectId]
  );

  const selectProject = useSessionStore((state) => state.selectProject);
  const renameProject = useSessionStore((state) => state.renameProject);
  const createProject = useSessionStore((state) => state.createProject);
  const exportProject = useSessionStore((state) => state.exportProject);
  const importProject = useSessionStore((state) => state.importProject);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(activeProject?.name ?? '');

  useEffect(() => {
    setName(activeProject?.name ?? '');
  }, [activeProject?.id, activeProject?.name]);

  const handleCreateProject = () => {
    const base = 'Untitled Project';
    const existing = new Set(projects.map((project) => project.name));
    let suffix = 1;
    let candidate = base;
    while (existing.has(candidate)) {
      candidate = `${base} ${suffix}`;
      suffix += 1;
    }
    createProject(candidate);
  };

  const handleNameBlur = () => {
    const trimmed = name.trim() || 'Untitled Project';
    if (activeProject && trimmed !== activeProject.name) {
      renameProject(activeProject.id, trimmed);
    }
    setName(trimmed);
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const projectData = JSON.parse(e.target?.result as string);
        importProject(projectData);
      } catch (error) {
        alert('Failed to import project. Please check the file format.');
      }
    };
    reader.readAsText(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <nav className="top-nav">
      <div className="top-nav-select">
        <label htmlFor="project-select">Naming Project</label>
        <select
          id="project-select"
          value={activeProjectId}
          onChange={(event) => selectProject(event.target.value)}
        >
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="icon-button"
          onClick={handleCreateProject}
          title="Create new project"
        >
          +
        </button>
        <button
          type="button"
          className="icon-button"
          onClick={exportProject}
          title="Export project"
        >
          ↓
        </button>
        <button
          type="button"
          className="icon-button"
          onClick={handleImport}
          title="Import project"
        >
          ↑
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
      <div className="top-nav-name">
        <input
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={handleNameBlur}
          placeholder="Project name"
        />
      </div>
    </nav>
  );
};
